"use client";

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Car,
  Wallet,
  Network,
  CheckCircle2,
  ChevronDown,
  TrendingUp,
  BarChart3,
  MapPin,
  DollarSign,
  Gauge,
  Users,
} from "lucide-react";
import { cn } from "@/lib/utils";
import MockGaragemShowcase from "@/components/landing/showcase/MockGaragemShowcase";
import MockFinanceiroShowcase from "@/components/landing/showcase/MockFinanceiroShowcase";
import MockRedeShowcase from "@/components/landing/showcase/MockRedeShowcase";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
type Callout = {
  label: string;
  icon: React.ElementType;
  /** Tailwind classes for position, e.g. "top-4 left-4" */
  position: string;
};

type ShowcaseTab = {
  id: string;
  label: string;
  icon: React.ElementType;
  bullets: string[];
  callouts: Callout[];
  /**
   * Prop para injetar imagem real futuramente.
   * Ex: imageSrc="/assets/screenshots/garagem.png"
   */
  imageSrc?: string;
};

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------
const TABS: ShowcaseTab[] = [
  {
    id: "garagem",
    label: "Garagem",
    icon: Car,
    bullets: [
      "Status em tempo real: Entrada → Disponível → Vendido",
      "Custo e margem calculados automaticamente por veículo",
      "Fotos, documentos e histórico centralizados em um card",
    ],
    callouts: [
      { label: "Status do veículo", icon: Gauge, position: "top-4 left-4" },
      { label: "Margem por veículo", icon: TrendingUp, position: "bottom-4 right-4" },
    ],
  },
  {
    id: "financeiro",
    label: "Financeiro",
    icon: Wallet,
    bullets: [
      "Fluxo de caixa de todas as lojas em um único painel",
      "Margem líquida calculada por veículo e por período",
      "DRE completo exportável em PDF com CMV detalhado",
    ],
    callouts: [
      { label: "Fluxo consolidado", icon: BarChart3, position: "top-4 right-4" },
      { label: "Margem líquida", icon: DollarSign, position: "bottom-4 left-4" },
    ],
  },
  {
    id: "rede",
    label: "Visão da rede",
    icon: Network,
    bullets: [
      "Comparativo de performance entre todas as lojas",
      "Ranking de vendedores por resultado no mês",
      "Alertas de veículos parados há mais de 30 dias",
    ],
    callouts: [
      { label: "Performance por loja", icon: MapPin, position: "top-4 left-4" },
      { label: "Ranking de vendedores", icon: Users, position: "bottom-4 right-4" },
    ],
  },
] as const;

// ---------------------------------------------------------------------------
// Animation variants
// ---------------------------------------------------------------------------
const contentVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] } },
  exit:   { opacity: 0, y: -8, transition: { duration: 0.2, ease: "easeIn" } },
} as const;

const accordionVariants = {
  collapsed: { height: 0, opacity: 0 },
  expanded:  { height: "auto", opacity: 1, transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] } },
} as const;

// ---------------------------------------------------------------------------
// ScreenshotArea — mock UI + callouts + suporte a imagem real
// ---------------------------------------------------------------------------
const ShowcaseMockBody = ({ tabId }: { tabId: string }) => {
  if (tabId === "garagem") return <MockGaragemShowcase />;
  if (tabId === "financeiro") return <MockFinanceiroShowcase />;
  if (tabId === "rede") return <MockRedeShowcase />;
  return <MockGaragemShowcase />;
};

const ScreenshotArea = ({ tab }: { tab: ShowcaseTab }) => (
  <div className="relative w-full aspect-video rounded-xl overflow-hidden border border-border/50 bg-zinc-950 shadow-[0_16px_48px_rgba(0,0,0,0.45)]">
    {tab.imageSrc ? (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={tab.imageSrc}
        alt={`Screenshot do módulo ${tab.label}`}
        className="h-full w-full object-cover"
      />
    ) : (
      <div className="flex h-full min-h-0 flex-col">
        <ShowcaseMockBody tabId={tab.id} />
      </div>
    )}

    {/* Scanline decorativo */}
    <div
      className="absolute inset-0 pointer-events-none opacity-[0.03]"
      style={{ backgroundImage: "repeating-linear-gradient(0deg, transparent, transparent 2px, #fff 2px, #fff 3px)" }}
      aria-hidden
    />

    {/* Callout badges */}
    {tab.callouts.map(({ label, icon: Icon, position }) => (
      <motion.div
        key={label}
        initial={{ opacity: 0, scale: 0.85 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        className={cn(
          "absolute z-20 flex items-center gap-2 px-3 py-2 rounded-xl",
          "bg-background/85 backdrop-blur-md border border-border/60 shadow-xl",
          position
        )}
      >
        {/* Pointer dot */}
        <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" aria-hidden />
        <Icon className="w-3.5 h-3.5 text-primary shrink-0" aria-hidden />
        <span className="text-[11px] font-semibold text-foreground whitespace-nowrap">
          {label}
        </span>
      </motion.div>
    ))}
  </div>
);

// ---------------------------------------------------------------------------
// BulletList
// ---------------------------------------------------------------------------
const BulletList = ({ bullets }: { bullets: string[] }) => (
  <ul className="flex flex-col gap-2.5" aria-label="Destaques do módulo">
    {bullets.map((bullet) => (
      <li key={bullet} className="flex items-start gap-2.5 text-sm text-muted-foreground">
        <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 shrink-0" aria-hidden />
        <span>{bullet}</span>
      </li>
    ))}
  </ul>
);

// ---------------------------------------------------------------------------
// Desktop Tabs
// ---------------------------------------------------------------------------
const DesktopShowcase = ({ tabs }: { tabs: ShowcaseTab[] }) => {
  const [activeId, setActiveId] = useState(tabs[0].id);
  const activeTab = tabs.find((t) => t.id === activeId) ?? tabs[0];

  return (
    <div className="hidden md:block">
      {/* Tab bar */}
      <div
        className="flex items-center gap-1 p-1 rounded-2xl bg-card/60 border border-border/50 mb-8 w-fit mx-auto"
        role="tablist"
        aria-label="Módulos do produto"
      >
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = tab.id === activeId;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-controls={`panel-${tab.id}`}
              id={`tab-${tab.id}`}
              onClick={() => setActiveId(tab.id)}
              className={cn(
                "relative flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 outline-none focus-visible:ring-2 focus-visible:ring-ring",
                isActive
                  ? "text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              {isActive && (
                <motion.span
                  layoutId="tab-pill"
                  className="absolute inset-0 rounded-xl bg-primary"
                  transition={{ type: "spring", stiffness: 380, damping: 32 }}
                  aria-hidden
                />
              )}
              <span className="relative z-10 flex items-center gap-2">
                <Icon className="w-4 h-4" aria-hidden />
                {tab.label}
              </span>
            </button>
          );
        })}
      </div>

      {/* Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeId}
          variants={contentVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          id={`panel-${activeId}`}
          role="tabpanel"
          aria-labelledby={`tab-${activeId}`}
          className="flex flex-col gap-6"
        >
          <ScreenshotArea tab={activeTab} />
          <div className="grid grid-cols-3 gap-4 px-1">
            <BulletList bullets={activeTab.bullets} />
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Mobile Accordion
// ---------------------------------------------------------------------------
const MobileAccordion = ({ tabs }: { tabs: ShowcaseTab[] }) => {
  const [openId, setOpenId] = useState<string | null>(tabs[0].id);

  const handleToggle = (id: string) => {
    setOpenId((prev) => (prev === id ? null : id));
  };

  return (
    <div className="md:hidden flex flex-col gap-2" role="list" aria-label="Módulos do produto">
      {tabs.map((tab) => {
        const Icon = tab.icon;
        const isOpen = openId === tab.id;

        return (
          <div
            key={tab.id}
            className="rounded-2xl border border-border/50 overflow-hidden bg-card/50"
            role="listitem"
          >
            {/* Accordion trigger */}
            <button
              type="button"
              onClick={() => handleToggle(tab.id)}
              aria-expanded={isOpen}
              aria-controls={`accordion-panel-${tab.id}`}
              id={`accordion-trigger-${tab.id}`}
              className={cn(
                "w-full flex items-center justify-between gap-3 px-5 py-4 text-left transition-colors duration-200",
                isOpen ? "text-foreground" : "text-muted-foreground hover:text-foreground"
              )}
            >
              <span className="flex items-center gap-3">
                <span
                  className={cn(
                    "w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-colors",
                    isOpen ? "bg-primary/15 text-primary" : "bg-muted/50 text-muted-foreground"
                  )}
                >
                  <Icon className="w-4 h-4" aria-hidden />
                </span>
                <span className="font-semibold text-sm">{tab.label}</span>
              </span>
              <ChevronDown
                className={cn(
                  "w-4 h-4 shrink-0 transition-transform duration-300",
                  isOpen && "rotate-180"
                )}
                aria-hidden
              />
            </button>

            {/* Accordion panel */}
            <AnimatePresence initial={false}>
              {isOpen && (
                <motion.div
                  id={`accordion-panel-${tab.id}`}
                  role="region"
                  aria-labelledby={`accordion-trigger-${tab.id}`}
                  key="panel"
                  variants={accordionVariants}
                  initial="collapsed"
                  animate="expanded"
                  exit="collapsed"
                  style={{ overflow: "hidden" }}
                >
                  <div className="px-5 pb-5 flex flex-col gap-4">
                    <ScreenshotArea tab={tab} />
                    <BulletList bullets={tab.bullets} />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------------------------
// ProductShowcase
// ---------------------------------------------------------------------------
export default function ProductShowcase() {
  return (
    <section
      id="como-funciona"
      className="relative z-10 py-24 bg-background overflow-hidden"
      aria-labelledby="showcase-headline"
    >
      {/* Background decoration */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[50%] h-[40%] bg-primary/6 rounded-full blur-[130px]" />
      </div>

      <div className="max-w-5xl mx-auto px-6 relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-12"
        >
          <h2
            id="showcase-headline"
            className="text-3xl md:text-5xl font-black tracking-tight text-foreground mb-4"
          >
            Veja o NoWey{" "}
            <span className="text-primary">na prática</span>
          </h2>
          <p className="text-base text-muted-foreground max-w-lg mx-auto">
            Navegue pelos módulos e veja como cada parte da sua operação ganha visibilidade.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.55, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
        >
          <DesktopShowcase tabs={TABS} />
          <MobileAccordion tabs={TABS} />
        </motion.div>
      </div>
    </section>
  );
}
